// src/services/guestOutbox.js — the guest chat's durable send queue.
//
// Everything the guest sends goes in here FIRST and leaves only when Firestore
// has the message. That single rule is what makes "sending 43%", "cancel",
// "failed — retry" and "not sent" possible for every message type instead of
// only for the two that happened to have a progress callback.
//
// 🟥 INDEXEDDB, NOT localStorage. A queued message owns the actual File the user
//    picked, and a File cannot be JSON-stringified — putting one through
//    localStorage yields "{}" and a Retry button that re-uploads nothing. IDB
//    stores Blobs natively via structured clone, so a 20MB video survives a
//    reload intact and Retry genuinely re-sends it. localStorage would also blow
//    its ~5MB quota on the first clip.
//
// 🟥 SCOPED BY GUEST UID. Two people can open two different links in the same
//    browser, and an anonymous uid is per-link. Without the scope, guest B would
//    inherit guest A's unsent photo and could send it into a conversation they
//    have nothing to do with. Every read filters on it; nothing is global.
//
// 🟥 AN INTERRUPTED SEND IS NEVER RESUMED AUTOMATICALLY. If the tab dies midway
//    we do not know which half happened: the upload may have completed and the
//    Firestore write may have landed a moment before the page went away. Silently
//    retrying that sends the message twice, and a duplicate is worse than a
//    prompt. So a job caught in 'sending' at startup becomes 'failed', with a
//    Retry the person can choose. Jobs still 'queued' never started, so those DO
//    resume on their own — there is nothing to duplicate.
//
// 🟢 AND A JOB IS RETIRED BY ITS clientTempId. Every send path stamps the message
//    document with the same id the job carries, so when the real doc arrives in
//    the live query the queue drops its copy. That is also the safety net for the
//    ambiguous case above: if an interrupted job's message DID land, its bubble
//    is retired by the incoming doc and the failed placeholder disappears on its
//    own, without anyone tapping anything.

const DB_NAME = 'allo-guest-outbox';
const DB_VERSION = 1;
const STORE = 'jobs';

/** Statuses, in the order a job travels through them. */
export const QUEUED = 'queued';
export const SENDING = 'sending';
export const FAILED = 'failed';

let _dbPromise = null;

/**
 * 🟢 EVERY CALLER TOLERATES "NO DATABASE". Private windows, storage pressure and
 *    browsers with site data switched off all reject open() — and none of that is
 *    a reason to stop the guest sending a message. openDb() resolves to null in
 *    those cases and the whole module degrades to an in-memory queue: sending
 *    still works, and only "survives a reload" is lost.
 */
export function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve) => {
    let req;
    try {
      if (typeof indexedDB === 'undefined') { resolve(null); return; }
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch { resolve(null); return; }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' });
        // read paths always filter by both, so index the pair
        os.createIndex('byOwner', ['guestUid', 'chatId'], { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => { console.warn('[outbox] indexedDB unavailable:', req.error?.message); resolve(null); };
    req.onblocked = () => resolve(null);
  });
  return _dbPromise;
}

const tx = async (mode, fn) => {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    let out;
    let t;
    try { t = db.transaction(STORE, mode); } catch (e) { reject(e); return; }
    t.oncomplete = () => resolve(out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('aborted'));
    try { out = fn(t.objectStore(STORE), (v) => { out = v; }); } catch (e) { reject(e); }
  });
};

/* ══ THE MIRROR ════════════════════════════════════════════════════════════
   🟥 THE UI READS THIS, NOT THE DATABASE. Re-reading IDB on every progress tick
      — several times a second, per message — would make uploading the slowest
      thing on the page. The authoritative copy is in memory; IDB is written
      behind it so a reload can rebuild the mirror. A progress percentage is
      deliberately NOT persisted on every tick (see patch): it is worth nothing
      after a reload, since an interrupted upload restarts from zero. */
let _mirror = [];
const _subs = new Set();

const emit = () => { const snap = _mirror.slice(); _subs.forEach((fn) => { try { fn(snap); } catch {} }); };

/** Subscribe to the queue. Returns an unsubscribe. */
export function watchOutbox(cb) {
  _subs.add(cb);
  cb(_mirror.slice());
  return () => _subs.delete(cb);
}

/** The queue as the UI should see it — this guest, this chat, oldest first. */
export function jobsFor(chatId, guestUid, all = _mirror) {
  return all
    .filter((j) => j.chatId === chatId && j.guestUid === guestUid)
    .sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * Rebuild the mirror from disk. Call once, when the chat opens.
 * Returns the jobs that are ready to run.
 */
export async function hydrate(chatId, guestUid) {
  let rows = [];
  try {
    rows = (await tx('readonly', (os, done) => {
      const r = os.getAll();
      r.onsuccess = () => done(r.result || []);
    })) || [];
  } catch (e) {
    console.warn('[outbox] hydrate failed:', e?.message);
    rows = [];
  }

  const mine = rows.filter((j) => j.guestUid === guestUid && j.chatId === chatId);
  const stale = rows.filter((j) => !mine.includes(j) && Date.now() - (j.createdAt || 0) > SEVEN_DAYS);
  // 🟢 housekeeping: another link's abandoned queue is somebody else's dead
  //    weight in this browser's storage. Nothing here is recoverable after a week.
  for (const j of stale) { remove(j.id).catch(() => {}); }

  _mirror = mine.map((j) => (j.status === SENDING
    // see the header: interrupted, so it must be a decision, not a silent resend
    ? { ...j, status: FAILED, pct: null, error: 'Interrupted — the page closed mid-send.' }
    : j));
  // persist the demotion so a second reload doesn't see 'sending' again
  for (const j of _mirror) {
    if (j.status === FAILED && mine.find((o) => o.id === j.id)?.status === SENDING) {
      put(j).catch(() => {});
    }
  }
  emit();
  return _mirror.filter((j) => j.status === QUEUED);
}

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

const put = (job) => tx('readwrite', (os) => { os.put(job); }).catch((e) => {
  console.warn('[outbox] write failed:', e?.message);
});

/**
 * Add a job. `payload` must be structured-cloneable — Files and Blobs are fine,
 * class instances and functions are not.
 */
export async function enqueue(job) {
  const row = {
    pct: 0,
    error: null,
    attempts: 0,
    status: QUEUED,
    ...job,
    createdAt: job.createdAt || Date.now(),
    updatedAt: Date.now(),
  };
  _mirror = [..._mirror, row];
  emit();
  await put(row);
  return row;
}

/**
 * Update a job.
 *
 * 🟢 `persist` DEFAULTS TO TRUE BUT PROGRESS PASSES FALSE. An upload reports
 *    progress dozens of times; each IDB write is a transaction, and none of them
 *    would ever be read — a resumed job starts its upload again from the
 *    beginning. So percentage moves in memory only, and status changes, which
 *    genuinely have to survive, are written.
 */
export function patch(id, fields, persist = true) {
  let next = null;
  _mirror = _mirror.map((j) => {
    if (j.id !== id) return j;
    next = { ...j, ...fields, updatedAt: Date.now() };
    return next;
  });
  if (!next) return null;
  emit();
  if (persist) put(next);
  return next;
}

/** Drop a job for good — sent, cancelled or discarded. */
export async function remove(id) {
  _mirror = _mirror.filter((j) => j.id !== id);
  emit();
  try { await tx('readwrite', (os) => { os.delete(id); }); } catch (e) {
    console.warn('[outbox] delete failed:', e?.message);
  }
}

/**
 * Retire every job whose message has now landed.
 *
 * 🟥 THIS IS WHAT PREVENTS A DUPLICATE BUBBLE. The optimistic row and the real
 *    Firestore document describe the same message; without matching them on
 *    clientTempId both would be drawn, and the queue would keep a 'failed' row
 *    for a message that is sitting in the conversation perfectly well.
 */
export function retire(liveTempIds) {
  const live = liveTempIds instanceof Set ? liveTempIds : new Set(liveTempIds || []);
  const gone = _mirror.filter((j) => live.has(j.id));
  if (!gone.length) return 0;
  gone.forEach((j) => { remove(j.id).catch(() => {}); });
  return gone.length;
}

/* ══ THE RUNNER ════════════════════════════════════════════════════════════
   One job at a time, in the order they were queued.

   🟥 STRICTLY SEQUENTIAL, ON PURPOSE. Three concurrent uploads on a phone's
      connection make all three slow and all three percentages jump about, and
      messages would arrive in an order the sender never chose — the second photo
      landing before the first because it was smaller. One at a time is also what
      makes "cancel" unambiguous: there is exactly one in-flight request. */
let _running = false;
const _aborts = new Map();       // job id → cancel() for its in-flight PUT

/**
 * @param send  async (job, { onProgress, registerAbort }) => void
 *              Resolves when the message is in Firestore; throws to fail the job.
 *              A cancelled upload rejects with a message containing 'CANCELLED'.
 * @param scope {chatId, guestUid} — REQUIRED in the app.
 *
 * 🟥 THE RUNNER IS SCOPED, NOT JUST THE READS. hydrate() only ever loads the
 *    current guest's rows, so in practice the mirror holds nothing else — but
 *    "in practice" is how a queue ends up sending one guest's photo into another
 *    guest's conversation. enqueue() does not filter, so a single misrouted call
 *    anywhere would be enough. The runner refuses to pick up work that is not
 *    addressed to the session asking for it, and that is a property of this
 *    function rather than an assumption about its callers.
 */
export async function runQueue(send, scope = null) {
  if (_running) return;
  _running = true;
  const inScope = (j) => !scope
    || (j.chatId === scope.chatId && j.guestUid === scope.guestUid);
  try {
    // re-read the mirror each pass: the user can queue more while this runs
    for (;;) {
      const next = _mirror
        .filter((j) => j.status === QUEUED && inScope(j))
        .sort((a, b) => a.createdAt - b.createdAt)[0];
      if (!next) break;
      patch(next.id, { status: SENDING, pct: 0, error: null, attempts: (next.attempts || 0) + 1 });
      try {
        // eslint-disable-next-line no-await-in-loop
        await send(_mirror.find((j) => j.id === next.id) || next, {
          onProgress: (f) => patch(next.id, { pct: Math.max(0, Math.min(100, Math.round(f * 100))) }, false),
          registerAbort: (cancel) => _aborts.set(next.id, cancel),
        });
        // 🟢 the job is NOT removed here. The live query retires it when the real
        //    document arrives, so the bubble never blinks out and back in.
        patch(next.id, { pct: 100, status: SENDING, error: null }, false);
      } catch (e) {
        const msg = String(e?.message || e || '');
        if (/CANCELLED/i.test(msg)) {
          // cancel() already removed the row; nothing to report
          // eslint-disable-next-line no-await-in-loop
          await remove(next.id);
        } else {
          patch(next.id, { status: FAILED, pct: null, error: friendlyError(msg) });
        }
      } finally {
        _aborts.delete(next.id);
      }
    }
  } finally {
    _running = false;
  }
}

/** Cancel an in-flight job, or drop a queued one. */
export async function cancel(id) {
  const abort = _aborts.get(id);
  if (abort) { try { abort(); } catch {} }
  _aborts.delete(id);
  await remove(id);
}

/** Put a failed job back in line. */
export function retry(id) {
  return patch(id, { status: QUEUED, pct: 0, error: null });
}

/**
 * 🟢 THE PERSON READING THIS IS NOT DEBUGGING OUR UPLOADER. "upload blocked — the
 *    R2 bucket needs a CORS rule allowing PUT from this origin" is the right thing
 *    to log and the wrong thing to put in a chat bubble. Each of these maps a real
 *    failure this code can produce to what the reader can actually do about it.
 */
export function friendlyError(raw) {
  const s = String(raw || '');
  if (/CORS|blocked/i.test(s)) return "Couldn't reach the server. Check your connection and try again.";
  if (/verify FAILED/i.test(s)) return 'The file arrived damaged. Try sending it again.';
  if (/secure|seal/i.test(s)) return "Couldn't encrypt that — it was not sent.";
  if (/network|Failed to fetch|offline/i.test(s)) return "You're offline. It will need another try once you're back.";
  if (/\(4\d\d\)/.test(s)) return 'The server refused that file. It may be too large.';
  if (/\(5\d\d\)/.test(s)) return 'The server had a problem. Try again in a moment.';
  if (/expired|ended/i.test(s)) return 'This temporary chat has ended.';
  return 'Not sent. Tap retry to try again.';
}

/** Test seam — resets module state between cases. */
export function __reset() {
  _mirror = [];
  _subs.clear();
  _aborts.clear();
  _running = false;
  _dbPromise = null;
}
